import React, { useEffect, useState } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { payroll } from '../../api';
import { currency, fmtDate } from '../billing/shared';

const thisTaxYear = () => { const d = new Date(); return d.getMonth() >= 2 ? d.getFullYear() + 1 : d.getFullYear(); };
const YEARS = [0, 1, 2].map((i) => thisTaxYear() - i);

export const TaxYearSelect = ({ value, onChange, testid }) => (
  <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
    <SelectTrigger className="w-44 bg-white" data-testid={testid}><SelectValue/></SelectTrigger>
    <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>Tax year {y - 1}/{String(y).slice(2)}</SelectItem>)}</SelectContent>
  </Select>
);

export const Emp201Tab = ({ clientId }) => {
  const [year, setYear] = useState(thisTaxYear());
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); payroll.emp201(clientId, year).then(setData); }, [clientId, year]);
  const total = data ? data.months.reduce((a, m) => a + m.totalPayable, 0) : 0;
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="payroll-emp201-tab">
      <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-stone-900">EMP201 monthly declarations</div>
          <div className="text-xs text-stone-500 mt-0.5">From finalised pay runs by pay date. Due on the 7th of the following month. ETI is offset against PAYE.</div>
        </div>
        <TaxYearSelect value={year} onChange={setYear} testid="emp201-year-select"/>
      </div>
      {!data ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="emp201-table">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>{['Month', 'PAYE', 'ETI used', 'PAYE after ETI', 'SDL', 'UIF', 'Total payable', 'Due', ''].map((h, i) => <th key={h || i} className={`px-4 py-3 font-medium ${i >= 1 && i <= 6 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.months.map((m) => {
                const empty = !m.runs && !m.takeOn;
                return (
                  <tr key={m.month} className={empty ? 'text-stone-400' : ''} data-testid={`emp201-row-${m.month}`}>
                    <td className="px-4 py-3 font-medium">{m.label}{m.takeOn && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600" data-testid={`emp201-takeon-${m.month}`}>Previous payroll</span>}{m.draftRuns > 0 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">{m.draftRuns} draft run</span>}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{currency(m.paye)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-sky-800">{m.etiUtilised ? `- ${currency(m.etiUtilised)}` : '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{currency(m.payeAfterEti)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{currency(m.sdl)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{currency(m.uif)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold" data-testid={`emp201-total-${m.month}`}>{currency(m.totalPayable)}</td>
                    <td className="px-4 py-3 text-xs">{fmtDate(m.dueDate)}</td>
                    <td className="px-4 py-3">{!empty && <a href={payroll.emp201Pdf(clientId, m.month)} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-emerald-800" title="EMP201 PDF" data-testid={`emp201-pdf-${m.month}`}><Download size={15}/></a>}</td>
                  </tr>
                );
              })}
              <tr className="bg-stone-50 font-semibold"><td className="px-4 py-3">Tax year total</td><td colSpan={5}/><td className="px-4 py-3 text-right tabular-nums" data-testid="emp201-year-total">{currency(total)}</td><td colSpan={2}/></tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const Emp501Tab = ({ clientId }) => {
  const [year, setYear] = useState(thisTaxYear());
  const [period, setPeriod] = useState('annual');
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); payroll.emp501(clientId, year, period).then(setData); }, [clientId, year, period]);
  const balanced = data && Object.values(data.difference).every((v) => Math.abs(v) < 0.01);

  return (
    <div className="space-y-6" data-testid="payroll-emp501-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <TaxYearSelect value={year} onChange={setYear} testid="emp501-year-select"/>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-56 bg-white" data-testid="emp501-period-select"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="interim">Interim (Mar – Aug)</SelectItem><SelectItem value="annual">Annual (Mar – Feb)</SelectItem></SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="emp501-pdf-btn"><a href={payroll.emp501Pdf(clientId, year, period)} target="_blank" rel="noreferrer"><Download size={14} className="mr-1.5"/> EMP501 PDF</a></Button>
          <Button variant="outline" size="sm" asChild data-testid="emp501-csv-btn"><a href={payroll.emp501Csv(clientId, year, period)}><FileSpreadsheet size={14} className="mr-1.5"/> Certificates CSV</a></Button>
        </div>
      </div>
      {!data ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
        <>
          <div className="rounded-xl bg-white border border-stone-200 p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-semibold text-stone-900">{data.periodLabel}</div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${balanced ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`} data-testid="emp501-balance-status">{balanced ? 'Reconciled — no differences' : 'Differences found'}</span>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[['PAYE', 'paye'], ['SDL', 'sdl'], ['UIF', 'uif']].map(([l, k]) => (
                <div key={k} className="rounded-lg border border-stone-200 p-4" data-testid={`emp501-recon-${k}`}>
                  <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{l}</div>
                  <div className="mt-1 text-sm flex justify-between"><span className="text-stone-500">EMP201s</span><span className="tabular-nums">{currency(data.declared[k])}</span></div>
                  <div className="text-sm flex justify-between"><span className="text-stone-500">Certificates</span><span className="tabular-nums">{currency(data.certificatesTotal[k])}</span></div>
                  <div className="text-sm flex justify-between font-semibold border-t border-stone-100 mt-1 pt-1"><span>Difference</span><span className="tabular-nums">{currency(data.difference[k])}</span></div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-stone-500">ETI claimed in period: {currency(data.declared.etiUtilised)} · Total paid over per EMP201s: {currency(data.declared.totalPayable)}</div>
          </div>
          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="p-5 border-b border-stone-100 font-semibold text-stone-900">Employee tax certificates (IRP5 / IT3(a))</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="irp5-table">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr>{['Employee', 'Type', 'Certificate no.', 'Periods', 'Gross (3699)', 'PAYE (4102)', 'UIF (4141)', ''].map((h, i) => <th key={h || i} className={`px-4 py-3 font-medium ${i >= 3 && i <= 6 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {data.certificates.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-stone-500">No finalised pay runs in this period.</td></tr>}
                  {data.certificates.map((c) => (
                    <tr key={c.employeeId} data-testid={`irp5-row-${c.employeeId}`}>
                      <td className="px-4 py-3 font-medium text-stone-900">{c.name}<div className="text-[11px] font-normal text-stone-500">{c.taxNumber || 'No tax number'}</div></td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${c.type === 'IRP5' ? 'bg-emerald-50 text-emerald-800' : 'bg-stone-100 text-stone-700'}`}>{c.type}</span></td>
                      <td className="px-4 py-3 font-mono text-xs">{c.certificateNumber}</td>
                      <td className="px-4 py-3 text-right">{c.periods}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{currency(c.grossIncome)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{currency(c.codes['4102'] || 0)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{currency(c.codes['4141'] || 0)}</td>
                      <td className="px-4 py-3"><a href={payroll.irp5Pdf(clientId, c.employeeId, year, period)} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-emerald-800" title="Certificate PDF" data-testid={`irp5-pdf-${c.employeeId}`}><Download size={15}/></a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
